import { useEffect, useState, FC, ReactElement } from 'react';

import { faCartShopping } from '@awesome.me/kit-379289146f/icons/classic/regular';
import { Drawer } from '@changegps/horizon-component-library';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack } from '@mui/material';
import {
  Elements,
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import { useBoolean } from 'usehooks-ts';
import { useLocation } from 'react-router-dom';

import 'swiper/css/navigation';

import { useGetResources } from '../hooks/use-get-products';

import { CheckoutButton, ConfirmProductSummary, ProductsTable } from '.';

import 'swiper/css';

import { useFormContext } from 'react-hook-form';
import { SubscriptionForm } from '../hooks';
import { authServiceAxiosClient } from '@/lib/axios';
import { useUserInfo } from '@/features/users';

const stripePromise = loadStripe(ENV.STRIPE_PUBLIC);

export const ProductTabs = () => {
  useGetResources();
  const { value: showFloatingCheckout, toggle: toggleFloatingCheckout } =
    useBoolean();
  const { value: showDrawer, toggle: toggleDrawer } = useBoolean();

  const [swiperController, setSwiperController] = useState<SwiperClass | null>(
    null,
  );

  const handleContinue = () => {
    swiperController?.slideNext();
    toggleFloatingCheckout();
  };

  const handleBack = () => {
    swiperController?.slidePrev();
    toggleFloatingCheckout();
  };

  const onClose = () => {
    toggleDrawer();
  };

  return (
    <Stack width="75vw">
      {!showFloatingCheckout && (
        <CheckoutButton onClick={() => handleContinue()}>
          <FontAwesomeIcon size="xl" icon={faCartShopping as IconProp} />
          Click me!
        </CheckoutButton>
      )}

      <Swiper
        modules={[Navigation]}
        onSwiper={setSwiperController}
        allowTouchMove={false}
        slidesPerView={1}
        spaceBetween={50}
        speed={400}
        autoHeight
        style={{ width: '100%' }}
      >
        <SwiperSlide>
          <ProductsTable />
        </SwiperSlide>
        <SwiperSlide>
          <ConfirmProductSummary
            toggleDrawer={toggleDrawer}
            action={() => handleBack()}
          />
        </SwiperSlide>
      </Swiper>

      <Drawer
        open={showDrawer}
        onClose={onClose}
        title={'Checkout'}
        content={
          <Elements stripe={stripePromise}>
            <div className="flex h-screen">
              <CheckoutForm />
            </div>
          </Elements>
        }
        actions={<></>}
      />
    </Stack>
  );
};

interface CheckoutResponse {
  clientSecret: string;
}

interface ProductInfo {
  productid: number;
  type: string;
  price: number;
  tierid: number;
  count: string | number;
}

type Param = {
  cid: string;
  coupon: string;
  productinfo: ProductInfo[];
};

const CheckoutForm: FC = (): ReactElement => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { watch } = useFormContext<SubscriptionForm>();
  const location = useLocation();
  const userInfo = useUserInfo(location.state === null);

  const data = watch();
  console.log('Checkout data:', data);

  const transformToParam = (data: any): Param => {
    const productinfo: ProductInfo[] = Object.entries(data)
      .filter(
        ([key, value]) =>
          key !== 'coupon' && value !== null && typeof value === 'object',
      )
      .map(([_, value]) => {
        const product = value as {
          product_id: string;
          type: string;
          price: number;
          tier_id: number;
          count?: string | number;
        };

        return {
          productid: (() => {
            const id = Number(product.product_id);
            if (id === 5) return 6;
            if (
              id === 3 &&
              (product.type === 'Advanced' ||
                product.type === 'Enterprise Advanced')
            )
              return 5;
            return id;
          })(),
          type: product.type,
          price: product.price,
          tierid: (() => {
            const id = Number(product.product_id);
            const tier_id = Number(product.tier_id);
            if (id === 5) return 1;
            return tier_id;
          })(),
          count: product.count || 0,
        };
      });

    // Get company ID from route state
    const routeState = location.state as { cid?: string } | null;

    return {
      cid:
        routeState && routeState.cid
          ? routeState.cid
          : (userInfo?.company_id ?? ''),
      coupon: data.coupon || '',
      productinfo,
    };
  };

  const param: Param = transformToParam(data);
  console.log('Checkout param:', param);

  const fetchClientSecret = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const res = await authServiceAxiosClient.post(
        '/subscription/create-checkout-session',
        param,
      );
      const responseData = res.data as CheckoutResponse;

      if (!responseData.clientSecret) {
        throw new Error('No client secret received from server');
      }

      setClientSecret(responseData.clientSecret);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to create checkout session';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchClientSecret();
  }, []);

  if (isLoading) {
    return <div>Loading checkout...</div>;
  }

  if (error) {
    return <div>Failed to load checkout: {error}</div>;
  }

  if (!clientSecret) {
    return <div>No checkout session available</div>;
  }

  const options = { clientSecret };

  return (
    <div id="checkout">
      <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};
